import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { updatePassword } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeOngoingProjects,
  subscribeProjects,
  subscribeExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  sendExpenseToAdmin,
  getExpenseSendRemainingMs,
  isExpenseSendDue,
  shouldHideFromEmployeeHistory,
  pauseExpenseSend,
} from '../services/expenseFirestore';
import { EXPENSE_STATUSES, EXPENSE_ADMIN_SEND_DELAY_MS } from '../constants/expenseConstants';
import SearchableDropdown from '../components/ui/SearchableDropdown';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import EmployeeExpenseHistory from '../components/expense/EmployeeExpenseHistory';
import ExpensePendingToast from '../components/expense/ExpensePendingToast';
import { TableSkeleton } from '../components/ui/Skeleton';
import './EmployeePortal.css';
import '../components/ui/Modal.css';
import '../components/expense/ExpensePendingToast.css';
import '../components/expense/ExpenseDashboard.css';

const EMPTY_ITEM = {
  id: '',
  expense: '',
  amount: '',
  status: 'Paid',
  serviceProvidedPersonName: '',
};

const createEmptyItem = () => ({
  ...EMPTY_ITEM,
  id: Date.now() + Math.random().toString(36).substr(2, 9),
});

const EMPTY_FORM = {
  projectDocId: '',
  projectId: '',
  projectName: '',
  projectStatus: '',
  date: new Date().toISOString().split('T')[0],
  items: [],
};

export default function EmployeePortal() {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('form');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState({ ...EMPTY_ITEM, projectDocId: '', projectId: '', projectName: '', date: '' });
  const [saving, setSaving] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const activeCountdownsRef = useRef(new Set());
  const sendingRef = useRef(new Set());

  // New states for item modal and drafts
  const [itemModal, setItemModal] = useState({ isOpen: false, editIndex: -1, item: createEmptyItem() });
  const [drafts, setDrafts] = useState([]);

  useEffect(() => {
    if (currentUser?.uid) {
      const stored = localStorage.getItem(`tvc_expense_drafts_${currentUser.uid}`);
      if (stored) {
        try {
          setDrafts(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to parse drafts', e);
        }
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const unsubP = subscribeOngoingProjects(setProjects);
    const unsubAll = subscribeProjects(setAllProjects);
    const unsubE = subscribeExpenses(
      (data) => {
        setLoadError('');
        setExpenses(data);
      },
      currentUser.uid,
      (err) => setLoadError(err.message || 'Failed to load expenses'),
    );
    setLoading(false);
    return () => { unsubP(); unsubAll(); unsubE(); };
  }, [currentUser]);

  const projectStatusByDocId = useMemo(
    () => Object.fromEntries(allProjects.map((p) => [p.id, p.status])),
    [allProjects],
  );

  const visibleExpenses = useMemo(
    () => expenses.filter((exp) => !shouldHideFromEmployeeHistory(exp, projectStatusByDocId[exp.projectDocId])),
    [expenses, projectStatusByDocId],
  );

  const projectOptions = useMemo(() =>
    projects.map((p) => ({
      value: p.id,
      label: `${p.projectId || '—'} — ${p.projectName}`,
      project: p,
    })),
  [projects]);

  const selectedProject = projects.find((p) => p.id === form.projectDocId);
  const noActiveProjects = projects.length === 0;
  const projectCompleted = selectedProject && !['Pending', 'In Progress', 'On Hold'].includes(selectedProject.status);

  const openEdit = useCallback(async (exp) => {
    if (exp.sentToAdmin === false && !exp.approved && !exp.sendPaused) {
      activeCountdownsRef.current.delete(exp.id);
      try {
        await pauseExpenseSend(exp.id);
      } catch (err) {
        toast.error(err.message || 'Failed to pause expense send');
        return;
      }
    }
    setEditForm({
      projectDocId: exp.projectDocId,
      projectId: exp.projectId,
      projectName: exp.projectName,
      projectStatus: '',
      date: exp.date || '',
      expense: exp.expense || '',
      amount: exp.amount || '',
      status: exp.status || 'Paid',
      serviceProvidedPersonName: exp.serviceProvidedPersonName || '',
    });
    setEditModal({ ...exp, sendPaused: true });
  }, []);

  const completeSendToAdmin = useCallback(async (expense) => {
    if (sendingRef.current.has(expense.id)) return;
    sendingRef.current.add(expense.id);
    try {
      await sendExpenseToAdmin(expense.id, expense.projectName);
      toast.success('Expense sent to admin for review');
    } catch (err) {
      toast.error(err.message || 'Failed to send expense to admin');
    } finally {
      sendingRef.current.delete(expense.id);
      activeCountdownsRef.current.delete(expense.id);
    }
  }, []);

  const showSendCountdown = useCallback((expense) => {
    if (expense.sentToAdmin !== false || expense.approved || expense.sendPaused) return;
    if (activeCountdownsRef.current.has(expense.id)) return;

    const remaining = getExpenseSendRemainingMs(expense);
    if (remaining <= 0) {
      completeSendToAdmin(expense);
      return;
    }

    activeCountdownsRef.current.add(expense.id);
    toast(
      ({ closeToast }) => (
        <ExpensePendingToast
          expense={expense}
          closeToast={closeToast}
          onCancel={() => {
            activeCountdownsRef.current.delete(expense.id);
            openEdit(expense);
          }}
          onComplete={() => completeSendToAdmin(expense)}
        />
      ),
      {
        autoClose: false,
        closeButton: false,
        className: 'exp-pending-toast-container',
      },
    );
  }, [completeSendToAdmin, openEdit]);

  useEffect(() => {
    expenses.forEach((exp) => {
      if (isExpenseSendDue(exp)) {
        completeSendToAdmin(exp);
      } else if (
        exp.sentToAdmin === false
        && !exp.approved
        && !exp.sendPaused
        && !activeCountdownsRef.current.has(exp.id)
        && !sendingRef.current.has(exp.id)
      ) {
        showSendCountdown(exp);
      }
    });
  }, [expenses, showSendCountdown, completeSendToAdmin]);

  const handleProjectSelect = (docId, opt) => {
    const p = opt?.project || projects.find((x) => x.id === docId);
    if (!p) return;
    setForm((f) => ({
      ...f,
      projectDocId: p.id,
      projectId: p.projectId || '',
      projectName: p.projectName || '',
      projectStatus: p.status || '',
    }));
  };

  const saveDraft = () => {
    if (!form.projectDocId && form.items.length === 0) {
      toast.warning('Nothing to save');
      return;
    }
    const newDraft = { ...form, draftId: Date.now().toString(), savedAt: new Date().toLocaleString() };
    const updatedDrafts = [...drafts, newDraft];
    setDrafts(updatedDrafts);
    localStorage.setItem(`tvc_expense_drafts_${currentUser.uid}`, JSON.stringify(updatedDrafts));
    toast.success('Saved as draft');
    setForm(EMPTY_FORM);
    setActiveSection('drafts');
  };

  const loadDraft = (draft) => {
    setForm(draft);
    setActiveSection('form');
  };

  const deleteDraft = (draftId) => {
    const updated = drafts.filter(d => d.draftId !== draftId);
    setDrafts(updated);
    localStorage.setItem(`tvc_expense_drafts_${currentUser.uid}`, JSON.stringify(updated));
  };

  const openItemModal = (index = -1) => {
    if (index === -1) {
      setItemModal({ isOpen: true, editIndex: -1, item: createEmptyItem() });
    } else {
      setItemModal({ isOpen: true, editIndex: index, item: { ...form.items[index] } });
    }
  };

  const closeItemModal = () => {
    setItemModal({ isOpen: false, editIndex: -1, item: createEmptyItem() });
  };

  const handleItemModalSave = () => {
    const { editIndex, item } = itemModal;
    if (!item.expense?.trim()) { toast.warning('Expense description is required'); return; }
    if (!item.amount || Number(item.amount) <= 0) { toast.warning('Valid amount is required'); return; }
    if (item.status === 'Unpaid') {
      const name = item.serviceProvidedPersonName?.trim() || '';
      if (name.length < 3) { toast.warning('Service provider name must be at least 3 characters'); return; }
    }

    setForm(f => {
      const newItems = [...f.items];
      if (editIndex === -1) {
        newItems.push(item);
      } else {
        newItems[editIndex] = item;
      }
      return { ...f, items: newItems };
    });
    closeItemModal();
  };

  const deleteItem = (index) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== index) }));
  };

  const validateForms = () => {
    if (!form.projectDocId) { toast.warning('Select a project'); return false; }
    if (projectCompleted) { toast.warning('Selected project is no longer active'); return false; }
    
    for (let i = 0; i < form.items.length; i++) {
      const item = form.items[i];
      if (!item.expense?.trim()) { toast.warning(`Row ${i + 1}: Expense description is required`); return false; }
      if (!item.amount || Number(item.amount) <= 0) { toast.warning(`Row ${i + 1}: Valid amount is required`); return false; }
      if (item.status === 'Unpaid') {
        const name = item.serviceProvidedPersonName?.trim() || '';
        if (name.length < 3) { toast.warning(`Row ${i + 1}: Service provider name must be at least 3 characters`); return false; }
      }
    }
    return true;
  };

  const validateEditForm = () => {
    if (!editForm.expense?.trim()) { toast.warning('Expense description is required'); return false; }
    if (!editForm.amount || Number(editForm.amount) <= 0) { toast.warning('Valid amount is required'); return false; }
    if (editForm.status === 'Unpaid') {
      const name = editForm.serviceProvidedPersonName?.trim() || '';
      if (name.length < 3) { toast.warning('Service provider name must be at least 3 characters'); return false; }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForms()) return;
    setSaving(true);
    try {
      const newExpenses = [];
      for (const item of form.items) {
        const expenseData = {
          projectDocId: form.projectDocId,
          projectId: form.projectId,
          projectName: form.projectName,
          expense: item.expense,
          amount: item.amount,
          date: form.date,
          status: item.status,
          serviceProvidedPersonName: item.status === 'Unpaid' ? item.serviceProvidedPersonName : '',
          employeeName: userProfile?.name || currentUser.email,
          approved: false,
          sentToAdmin: false,
        };
        const expenseId = await createExpense(expenseData, currentUser.uid);

        newExpenses.push({
          id: expenseId,
          employeeId: currentUser.uid,
          ...expenseData,
          amount: Number(item.amount),
          sendPaused: false,
          adminSendAt: new Date(Date.now() + EXPENSE_ADMIN_SEND_DELAY_MS).toISOString(),
          createdAt: new Date().toISOString(),
        });
      }

      setExpenses((prev) => {
        const newIds = new Set(newExpenses.map(e => e.id));
        return [...newExpenses, ...prev.filter((item) => !newIds.has(item.id))];
      });
      setLoadError('');

      toast.success(`${newExpenses.length} expense${newExpenses.length > 1 ? 's' : ''} added to history`);
      setForm(EMPTY_FORM);
      setActiveSection('history');
      newExpenses.forEach(showSendCountdown);
    } catch (err) {
      toast.error(err.message || 'Failed to submit expenses');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editModal || !validateEditForm()) return;
    if (editModal.approved) {
      toast.warning('Approved expenses cannot be edited');
      return;
    }
    setSaving(true);
    try {
      const updates = {
        expense: editForm.expense,
        amount: editForm.amount,
        date: editForm.date,
        status: editForm.status,
        serviceProvidedPersonName: editForm.status === 'Unpaid' ? editForm.serviceProvidedPersonName : '',
      };
      if (editModal.sentToAdmin === false) {
        updates.adminSendAt = new Date(Date.now() + EXPENSE_ADMIN_SEND_DELAY_MS).toISOString();
        updates.sentToAdmin = false;
        updates.sendPaused = false;
      }
      await updateExpense(editModal.id, updates);
      toast.success('Expense updated');
      const updatedExpense = { ...editModal, ...updates, id: editModal.id };
      setEditModal(null);
      setEditForm(EMPTY_FORM);
      if (updatedExpense.sentToAdmin === false) {
        activeCountdownsRef.current.delete(updatedExpense.id);
        showSendCountdown(updatedExpense);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.approved) {
      toast.warning('Approved expenses cannot be deleted');
      setDeleteTarget(null);
      return;
    }
    try {
      await deleteExpense(deleteTarget.id);
      activeCountdownsRef.current.delete(deleteTarget.id);
      toast.success('Expense deleted');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.warning('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warning('Passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await updatePassword(currentUser, newPassword);
      toast.success('Password updated successfully. Please log in again.');
      setPasswordModalOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      await logout();
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        toast.error('Please log out and log in again to change your password.');
      } else {
        toast.error(err.message || 'Failed to update password');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const navigateSection = (section) => {
    setActiveSection(section);
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/');
  };

  return (
    <div className="emp-portal">
      <button type="button" className="emp-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle navigation">
        <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'}`} />
      </button>

      <div
        className={`emp-sidebar-backdrop ${sidebarOpen ? 'is-visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      <aside className={`emp-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="emp-brand">
          <p className="emp-eyebrow">TVC Employee Portal</p>
          <h2>{userProfile?.name || 'Employee'}</h2>
          <p className="emp-email">{currentUser?.email}</p>
        </div>
        <nav className="emp-nav">
          <button type="button" className={activeSection === 'form' ? 'is-active' : ''} onClick={() => navigateSection('form')}>
            <i className="fas fa-plus-circle" /> Add Expense
          </button>
          <button type="button" className={activeSection === 'history' ? 'is-active' : ''} onClick={() => navigateSection('history')}>
            <i className="fas fa-history" /> Expense History
          </button>
          <button type="button" className={activeSection === 'drafts' ? 'is-active' : ''} onClick={() => navigateSection('drafts')}>
            <i className="fas fa-file-alt" /> Drafts ({drafts.length})
          </button>
          <button type="button" onClick={() => { setPasswordModalOpen(true); setSidebarOpen(false); }}>
            <i className="fas fa-key" /> Change Password
          </button>
        </nav>
        <button type="button" className="emp-logout" onClick={handleLogout}>
          <i className="fas fa-sign-out-alt" /> Log out
        </button>
      </aside>

      <main className="emp-main">
        <header className="emp-header">
          <h1>{activeSection === 'form' ? 'Submit Expense' : activeSection === 'drafts' ? 'My Drafts' : 'My Expenses'}</h1>
          <p>{activeSection === 'form' ? 'Add expenses for active projects only' : activeSection === 'drafts' ? 'Resume or delete your saved drafts' : 'View and manage your submitted expenses'}</p>
        </header>

        {loading ? (
          <TableSkeleton rows={4} cols={5} />
        ) : activeSection === 'drafts' ? (
          <div className="emp-panel">
            {drafts.length === 0 ? (
              <div className="emp-alert">No drafts found.</div>
            ) : (
              <div className="ui-data-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Saved At</th>
                      <th>Items</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drafts.map(d => (
                      <tr key={d.draftId}>
                        <td>{d.projectName}</td>
                        <td>{d.savedAt}</td>
                        <td>{d.items.length}</td>
                        <td>
                          <button type="button" className="ui-btn ui-btn-primary" style={{ padding: '0.3rem 0.6rem', fontSize: '0.85rem', marginRight: '0.5rem' }} onClick={() => loadDraft(d)}>Resume</button>
                          <button type="button" className="ui-btn ui-btn-secondary" style={{ padding: '0.3rem 0.6rem', fontSize: '0.85rem', color: '#dc3545', borderColor: '#dc3545' }} onClick={() => deleteDraft(d.draftId)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : activeSection === 'form' ? (
          <div className="emp-panel">
            {noActiveProjects && (
              <div className="emp-alert">No active projects available</div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="ui-form-row">
                <div className="ui-form-group">
                  <label>Project Name *</label>
                  <SearchableDropdown
                    options={projectOptions}
                    value={form.projectDocId}
                    onChange={handleProjectSelect}
                    placeholder="Search project…"
                    disabled={noActiveProjects}
                    emptyMessage="No active projects available"
                  />
                </div>
                <div className="ui-form-group">
                  <label>Project ID</label>
                  <input type="text" value={form.projectId} readOnly placeholder="Auto-filled" />
                </div>
              </div>
              <div className="ui-form-row">
                <div className="ui-form-group">
                  <label>Project Status</label>
                  <input type="text" value={form.projectStatus} readOnly />
                </div>
                <div className="ui-form-group">
                  <label>Date *</label>
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                </div>
              </div>

              {projectCompleted && (
                <div className="emp-alert danger">Selected project is no longer active. Please choose another project.</div>
              )}

              <div className="expense-items-container">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', marginBottom: '10px' }}>
                  <h3 style={{ fontSize: '1.1rem', color: '#333', margin: 0 }}>Expense Details</h3>
                  <button type="button" className="ui-btn ui-btn-secondary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.9rem' }} onClick={() => openItemModal(-1)} disabled={noActiveProjects}>
                    <i className="fas fa-plus"></i> Add Expense
                  </button>
                </div>
                {form.items.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
                    No expenses added yet. Click "Add Expense" to start.
                  </div>
                ) : (
                  <div className="ui-data-table-wrap" style={{ border: '1px solid rgba(15,37,84,0.12)', borderRadius: '8px' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Expense</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Service Person</th>
                          <th style={{ width: '80px', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.items.map((item, index) => (
                          <tr key={item.id || index}>
                            <td>{item.expense}</td>
                            <td>₹{item.amount}</td>
                            <td>{item.status}</td>
                            <td>{item.status === 'Unpaid' ? item.serviceProvidedPersonName : '—'}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <button type="button" className="emp-icon-btn" onClick={() => openItemModal(index)} title="Edit">
                                <i className="fas fa-edit"></i>
                              </button>
                              <button type="button" className="emp-icon-btn danger" onClick={() => deleteItem(index)} title="Delete" style={{ marginLeft: '4px' }}>
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
                <button type="button" className="ui-btn ui-btn-secondary" onClick={saveDraft}>
                  <i className="fas fa-save"></i> Save Draft
                </button>
                <button type="submit" className="ui-btn ui-btn-primary" disabled={saving || noActiveProjects || projectCompleted || form.items.length === 0}>
                  {saving ? 'Submitting…' : `Submit ${form.items.length} Expense${form.items.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="emp-panel">
            {loadError && (
              <div className="emp-alert danger">{loadError}</div>
            )}
            <EmployeeExpenseHistory
              expenses={visibleExpenses}
              onEdit={(exp) => {
                openEdit(exp);
                setSidebarOpen(false);
              }}
              onDelete={setDeleteTarget}
            />
          </div>
        )}
      </main>

      <Modal
        open={!!editModal}
        onClose={() => { setEditModal(null); setEditForm(EMPTY_FORM); }}
        title="Edit Expense"
        footer={
          <>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => { setEditModal(null); setEditForm(EMPTY_FORM); }}>Cancel</button>
            <button type="button" className="ui-btn ui-btn-primary" onClick={handleUpdate} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </>
        }
      >
        <div className="ui-form-group"><label>Project</label><input readOnly value={`${editForm.projectId} — ${editForm.projectName}`} /></div>
        <div className="ui-form-row">
          <div className="ui-form-group"><label>Date</label><input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} /></div>
          <div className="ui-form-group"><label>Amount</label><input type="number" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} /></div>
        </div>
        <div className="ui-form-group"><label>Expense</label><input value={editForm.expense} onChange={(e) => setEditForm({ ...editForm, expense: e.target.value })} /></div>
        <div className="ui-form-group">
          <label>Status</label>
          <div className="emp-status-switch-container">
            <label className="custom-bs-toggle">
              <input
                type="checkbox"
                checked={editForm.status === 'Paid'}
                onChange={(e) => {
                  const val = e.target.checked ? 'Paid' : 'Unpaid';
                  setEditForm({ ...editForm, status: val, serviceProvidedPersonName: val === 'Paid' ? '' : editForm.serviceProvidedPersonName });
                }}
              />
              <div className="toggle-inner">
                <div className="toggle-on">Paid</div>
                <div className="toggle-handle"></div>
                <div className="toggle-off">Unpaid</div>
              </div>
            </label>
          </div>
        </div>
        {editForm.status === 'Unpaid' && (
          <div className="ui-form-group ui-field-animate">
            <label>Service Provided Person Name</label>
            <input value={editForm.serviceProvidedPersonName} onChange={(e) => setEditForm({ ...editForm, serviceProvidedPersonName: e.target.value })} minLength={3} required />
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Expense"
        message="Are you sure you want to delete this expense? This action cannot be undone."
        confirmLabel="Delete"
      />

      <Modal
        open={itemModal.isOpen}
        onClose={closeItemModal}
        title={itemModal.editIndex === -1 ? 'Add Expense' : 'Edit Expense'}
        footer={
          <>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={closeItemModal}>Cancel</button>
            <button type="button" className="ui-btn ui-btn-primary" onClick={handleItemModalSave}>Save</button>
          </>
        }
      >
        <div className="ui-form-group">
          <label>Expense Description *</label>
          <input
            type="text"
            value={itemModal.item.expense}
            onChange={(e) => setItemModal(m => ({ ...m, item: { ...m.item, expense: e.target.value } }))}
            placeholder="Describe the expense"
          />
        </div>
        <div className="ui-form-row">
          <div className="ui-form-group">
            <label>Amount (₹) *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={itemModal.item.amount}
              onChange={(e) => setItemModal(m => ({ ...m, item: { ...m.item, amount: e.target.value } }))}
            />
          </div>
          <div className="ui-form-group">
            <label>Status *</label>
            <div className="emp-status-switch-container">
              <label className="custom-bs-toggle">
                <input
                  type="checkbox"
                  checked={itemModal.item.status === 'Paid'}
                  onChange={(e) => {
                    const val = e.target.checked ? 'Paid' : 'Unpaid';
                    setItemModal(m => ({
                      ...m,
                      item: {
                        ...m.item,
                        status: val,
                        serviceProvidedPersonName: val === 'Paid' ? '' : m.item.serviceProvidedPersonName
                      }
                    }));
                  }}
                />
                <div className="toggle-inner">
                  <div className="toggle-on">Paid</div>
                  <div className="toggle-handle"></div>
                  <div className="toggle-off">Unpaid</div>
                </div>
              </label>
            </div>
          </div>
        </div>
        {itemModal.item.status === 'Unpaid' && (
          <div className="ui-form-group ui-field-animate">
            <label>Service Provided Person Name *</label>
            <input
              type="text"
              value={itemModal.item.serviceProvidedPersonName}
              onChange={(e) => setItemModal(m => ({ ...m, item: { ...m.item, serviceProvidedPersonName: e.target.value } }))}
              placeholder="Enter name"
            />
          </div>
        )}
      </Modal>

      <Modal
        open={passwordModalOpen}
        onClose={() => { setPasswordModalOpen(false); setNewPassword(''); setConfirmPassword(''); }}
        title="Change Password"
        footer={
          <>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => { setPasswordModalOpen(false); setNewPassword(''); setConfirmPassword(''); }}>Cancel</button>
            <button type="button" className="ui-btn ui-btn-primary" onClick={handleChangePassword} disabled={changingPassword}>
              {changingPassword ? 'Updating…' : 'Update Password'}
            </button>
          </>
        }
      >
        <div className="ui-form-group">
          <label>New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password (min. 6 characters)"
          />
        </div>
        <div className="ui-form-group">
          <label>Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
          />
        </div>
      </Modal>
    </div>
  );
}